import { Box, Stack, Typography } from "@mui/material";
import { StatusChip, type StatusChipColor } from "@/components/common/fields/StatusChipField";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import type { ASDStatus, Policy } from "@/types";

/**
 * Aggregate Specific Deductible corridor context for the Claims Summary (#3302).
 *
 * On an ASD policy, claimants can be over their specific deductible while the carrier
 * owes nothing — every dollar of excess is absorbed by the aggregate corridor until it
 * is exhausted. Without this banner the exhibit shows a column of $0 Owed with no
 * explanation, which reads as broken data rather than "corridor still accumulating."
 *
 * Corridor state comes from the ASD engine (Feature #2934): `policies.asd_tracking`
 * carries the remaining balance and status; the threshold itself is
 * `specific_benefit.aggregate_specific_deductible`. Consumed is derived, since the
 * engine persists remaining rather than consumed.
 */

const STATUS_LABEL: Record<ASDStatus, string> = {
  accumulating: "Accumulating",
  breached: "Breached",
  unbreached: "Un-breached",
};

/**
 * accumulating — corridor absorbing, carrier pays nothing yet.
 * breached — corridor exhausted, carrier paying.
 * unbreached — total receded back below the ASD; a high-water mark is latched and
 * payments are frozen until it is re-cleared (#3335/#3336), so this is a warning.
 */
const STATUS_COLOR: Record<ASDStatus, StatusChipColor> = {
  accumulating: "info",
  breached: "success",
  unbreached: "warning",
};

const Figure = ({ label, value }: { label: string; value?: number }) => (
  <Box sx={{ minWidth: 130 }}>
    <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2, display: "block" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
      {value === undefined ? "—" : formatUSD(value)}
    </Typography>
  </Box>
);

export const AggSpecCorridorBanner = ({ policy }: { policy: Policy }) => {
  const asd = policy.specific_benefit?.aggregate_specific_deductible;
  const tracking = policy.asd_tracking;
  const remaining = tracking?.asd_remaining;

  // The engine persists remaining, not consumed. Only derive when both sides are
  // known — otherwise show a dash rather than implying the corridor is untouched.
  const consumed = asd !== undefined && remaining !== undefined ? asd - remaining : undefined;

  const status = tracking?.asd_status;

  return (
    <Box
      sx={{
        mb: 1.5,
        px: 2,
        py: 1.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        backgroundColor: "action.hover",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="overline" sx={{ fontSize: "0.6rem", color: "text.disabled", letterSpacing: "0.1em" }}>
          Aggregate Specific Deductible
        </Typography>
        {status && <StatusChip label={STATUS_LABEL[status] ?? status} color={STATUS_COLOR[status] ?? "default"} />}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
        <Figure label="Agg Spec Deductible" value={asd} />
        <Figure label="Applied to Date" value={consumed} />
        <Figure label="Remaining" value={remaining} />
        {tracking?.amt_high_water_mark !== undefined && (
          <Figure label="High Water Mark" value={tracking.amt_high_water_mark} />
        )}
      </Stack>

      {status === "accumulating" && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Claimants over their specific deductible show $0 owed while the corridor absorbs their excess. The carrier
          begins reimbursing once the aggregate specific deductible is exhausted.
        </Typography>
      )}
      {status === "unbreached" && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Cumulative excess has receded below the aggregate specific deductible. Carrier payments are frozen at the high
          water mark until it is re-cleared.
        </Typography>
      )}
    </Box>
  );
};
